import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Upload, Loader2, CheckCircle2, Building2, FileText, Camera } from 'lucide-react';

interface DocUpload { label: string; field: string; required: boolean; description?: string; }

const requiredDocs: DocUpload[] = [
  { label: 'Business License', field: 'trade_license_url', required: true },
  { label: 'Trade Registration', field: 'trade_name_registration_url', required: true },
  { label: 'TIN Certificate', field: 'tin_certificate_url', required: true },
  { label: 'VAT Certificate', field: 'vat_certificate_url', required: false, description: 'If applicable' },
  { label: 'Agency Logo', field: 'logo_url', required: true, description: 'High quality logo image' },
];

export default function AgencyApplication() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [agencyName, setAgencyName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [address, setAddress] = useState('');

  const [docs, setDocs] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  if (!user) { navigate('/login'); return null; }

  const uploadDoc = async (file: File, field: string) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({ variant: 'destructive', title: 'Invalid file', description: 'Only PDF, JPG, PNG, WEBP allowed' });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ variant: 'destructive', title: 'File too large', description: 'Max 15MB' });
      return;
    }
    setUploading(prev => ({ ...prev, [field]: true }));
    const ext = file.name.split('.').pop() || 'pdf';
    const path = `${user.id}/${field}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('developer-documents').upload(path, file, { cacheControl: '3600' });
    if (error) toast({ variant: 'destructive', title: 'Upload failed', description: error.message });
    else setDocs(prev => ({ ...prev, [field]: path }));
    setUploading(prev => ({ ...prev, [field]: false }));
  };

  const handleSubmit = async () => {
    if (!agencyName.trim()) {
      toast({ variant: 'destructive', title: 'Agency name is required' });
      return;
    }
    if (!managerName.trim()) {
      toast({ variant: 'destructive', title: 'Manager name is required' });
      return;
    }
    const missing = requiredDocs.filter(d => d.required && !docs[d.field]);
    if (missing.length > 0) {
      toast({ variant: 'destructive', title: 'Missing required documents', description: `${missing.length} required documents remaining` });
      return;
    }

    setSubmitting(true);
    const { data: insertedApp, error } = await supabase.from('agency_applications' as any).insert({
      user_id: user.id,
      agency_name: agencyName,
      manager_name: managerName,
      address: address || null,
      ...docs,
    }).select('id').single();
    setSubmitting(false);

    if (error) {
      toast({ variant: 'destructive', title: 'Submission failed', description: error.message });
    } else {
      // Notify reviewers (superadmins + sales team)
      const { data: reviewers } = await supabase
        .from('user_roles')
        .select('user_id')
        .in('role', ['superadmin', 'sales_manager', 'sales_team_leader', 'sales_agent'] as any);
      if (reviewers?.length) {
        await supabase.from('notifications').insert(
          reviewers.map((r: any) => ({
            user_id: r.user_id,
            title: 'New agency application',
            message: `${agencyName} submitted an agency application for review.`,
            type: 'application_submitted',
            metadata: { application_id: (insertedApp as any)?.id, application_type: 'agency' },
          }))
        );
      }
      toast({ title: 'Application Submitted!', description: 'Our team will review your application. You will be notified once approved.' });
      navigate('/dashboard');
    }
  };

  return (
    <Layout>
      <div className="container max-w-3xl py-10">
        <div className="text-center mb-8">
          <Building2 className="h-12 w-12 mx-auto text-primary mb-3" />
          <h1 className="font-display text-2xl md:text-3xl font-bold">Agency Registration</h1>
          <p className="text-muted-foreground mt-2">Submit your application to register your agency on EthioRealEstates.</p>
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><Building2 className="h-5 w-5 text-primary" /> Agency Information</CardTitle>
              <CardDescription>Basic information about your agency</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Agency Name <span className="text-destructive">*</span></Label><Input value={agencyName} onChange={e => setAgencyName(e.target.value)} placeholder="Your agency name" /></div>
                <div><Label>Manager Name <span className="text-destructive">*</span></Label><Input value={managerName} onChange={e => setManagerName(e.target.value)} placeholder="Full name of manager" /></div>
              </div>
              <div><Label>Address</Label><Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Office address" /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><FileText className="h-5 w-5 text-primary" /> Documents & Logo</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {requiredDocs.map(doc => (
                <div key={doc.field} className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{doc.label} {doc.required && <span className="text-destructive">*</span>}</div>
                    {doc.description && <div className="text-xs text-muted-foreground">{doc.description}</div>}
                  </div>
                  {docs[doc.field] ? (
                    <div className="flex items-center gap-2 text-sm text-green-600"><CheckCircle2 className="h-4 w-4" /><span className="hidden sm:inline">Uploaded</span></div>
                  ) : uploading[doc.field] ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <label className="cursor-pointer">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 transition-colors">
                        <Upload className="h-3.5 w-3.5" /> Upload
                      </div>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadDoc(f, doc.field); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="gap-2 min-w-[200px]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
